import React, { useState } from 'react';
import { FaEnvelope, FaSearch, FaCalendarAlt, FaSortAmountDown, FaSortAmountUp, FaTrash, FaDownload } from 'react-icons/fa';
import { format } from 'date-fns';

const ContactSubmissions = ({ submissions: initialSubmissions }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortOrder, setSortOrder] = useState('newest');
  const [submissions, setSubmissions] = useState(initialSubmissions || []);
  const [bin, setBin] = useState(() => JSON.parse(localStorage.getItem('contactSubmissionsBin') || '[]'));
  const [showBin, setShowBin] = useState(false);
  const [modal, setModal] = useState(null);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const activeList = showBin ? bin : submissions;
  const totalPages = Math.ceil(activeList.filter(submission =>
    submission.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    submission.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (submission.phone || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (submission.subject || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    submission.message.toLowerCase().includes(searchTerm.toLowerCase())
  ).length / pageSize) || 1;

  // Move to bin
  const handleDelete = (id) => {
    const toDelete = submissions.find(s => s.id === id);
    if (toDelete) {
      const updated = submissions.filter(s => s.id !== id);
      setSubmissions(updated);
      localStorage.setItem('contactSubmissions', JSON.stringify(updated));
      const newBin = [toDelete, ...bin];
      setBin(newBin);
      localStorage.setItem('contactSubmissionsBin', JSON.stringify(newBin));
    }
  };
  // Restore from bin
  const handleRestore = (id) => {
    const toRestore = bin.find(s => s.id === id);
    if (toRestore) {
      const updatedBin = bin.filter(s => s.id !== id);
      setBin(updatedBin);
      localStorage.setItem('contactSubmissionsBin', JSON.stringify(updatedBin));
      const newSubs = [toRestore, ...submissions];
      setSubmissions(newSubs);
      localStorage.setItem('contactSubmissions', JSON.stringify(newSubs));
    }
  };
  // Permanently delete from bin
  const handlePermanentDelete = (id) => {
    const updatedBin = bin.filter(s => s.id !== id);
    setBin(updatedBin);
    localStorage.setItem('contactSubmissionsBin', JSON.stringify(updatedBin));
  };



  // Export to CSV
  const handleExportCSV = () => {
    const csvHeader = ['Name', 'Email', 'Phone', 'Subject', 'Message', 'Date'];
    const csvRows = submissions.map(s => [
      s.name, s.email, s.phone || '', s.subject || '', s.message.replace(/\n/g, ' '), s.date
    ]);
    const csvContent = [csvHeader, ...csvRows].map(row => row.map(field => '"' + (field || '').replace(/"/g, '""') + '"').join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contact_submissions.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  
  // Sort and filter submissions
  const filteredSubmissions = [...submissions]
    .filter(submission => 
      submission.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      submission.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (submission.phone || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (submission.subject || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      submission.message.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortField === 'date') {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
      } else if (sortField === 'name') {
        return sortOrder === 'newest' 
          ? b.name.localeCompare(a.name) 
          : a.name.localeCompare(b.name);
      } else if (sortField === 'subject') {
        return sortOrder === 'newest' 
          ? (b.subject || '').localeCompare(a.subject || '') 
          : (a.subject || '').localeCompare(b.subject || '');
      }
      return 0;
    });
  
  // Toggle sort order
  const toggleSortOrder = () => {
    setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest');
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return format(date, 'MMM d, yyyy h:mm a');
    } catch (error) {
      return dateString;
    }
  };
  
  return (
    <div className="mb-12 bg-white  p-6 rounded-xl shadow-md border border-slate-300 ">
      <h2 className="text-2xl font-bold mb-6 text-center text-purple-700 ">
        <div className="flex items-center justify-center gap-2">
          <FaEnvelope className="text-purple-600 " />
          <span>Contact Form Submissions</span>
        </div>
      </h2>
      
      {/* Search, Sort, Export Controls */}
      <div className="flex flex-col md:flex-row gap-4 mb-6 items-center">
        <div className="relative flex-grow">
          <input
            type="text"
            placeholder="Search by name, email, phone, subject, or message"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300  rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 w-full bg-white  text-slate-800 "
          />
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 " />
        </div>
        <div className="flex gap-2 items-center">
          <select
            value={sortField}
            onChange={e => setSortField(e.target.value)}
            className="border border-gray-300  rounded px-2 py-1 focus:ring-2 focus:ring-purple-500 bg-white  text-slate-800 "
          >
            <option value="date">Sort by Date</option>
            <option value="name">Sort by Name</option>
            <option value="subject">Sort by Subject</option>
          </select>
          <button
            onClick={toggleSortOrder}
            className="flex items-center justify-center gap-2 bg-purple-100  hover:bg-purple-200 :bg-purple-950/50 text-purple-800  px-3 py-2 rounded-lg transition-colors duration-300"
          >
            {sortOrder === 'newest' ? <FaSortAmountDown /> : <FaSortAmountUp />}
          </button>
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 bg-green-100  hover:bg-green-200 :bg-green-950/50 text-green-800  px-3 py-2 rounded-lg transition-colors duration-300"
          >
            <FaDownload /> Export CSV
          </button>
        </div>
      </div> 
      
      {/* Results Count */}
      <div className="mb-4 text-sm text-gray-600 ">
        Showing {filteredSubmissions.length} of {submissions.length} submissions
      </div>
      
      {/* Submissions List */}
      {filteredSubmissions.length === 0 ? (
        <div className="text-center py-8 bg-gray-50  rounded-lg border border-dashed border-purple-200 ">
          <p className="text-purple-400  text-lg font-semibold">No contact submissions found.</p>
        </div>
      ) : (
        <>
          <div className="mb-6 flex justify-between items-center">
            <button
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow ${showBin ? 'bg-red-100  text-red-700 ' : 'bg-purple-100  text-purple-700 '}`}
              onClick={()=>{setShowBin(v=>!v); setPage(1);}}
            >
              {showBin ? <FaTrash /> : <FaEnvelope />}
              {showBin ? 'Show Active' : 'Show Bin'}
            </button>
            {showBin && <span className="text-red-500  font-bold flex items-center gap-2"><FaTrash /> Trash Bin ({bin.length})</span>}
          </div>

          <div className="space-y-4">
            {activeList.filter(submission =>
              submission.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              submission.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
              (submission.phone || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
              (submission.subject || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
              submission.message.toLowerCase().includes(searchTerm.toLowerCase())
            ).slice((page-1)*pageSize, page*pageSize).map((submission) => (
              <div 
                key={submission.id} 
                className={`border ${showBin ? 'border-red-300 ' : 'border-gray-300 '} rounded-xl overflow-hidden hover:shadow-xl transition-shadow duration-300 bg-white  cursor-pointer group relative`}
                onClick={() => setModal(submission)}
              >
                <div className={`flex flex-col md:flex-row md:items-center md:justify-between gap-2 ${showBin ? 'bg-gradient-to-r from-red-50 to-orange-50  ' : 'bg-gradient-to-r from-purple-50 to-indigo-50  '} p-4 border-b border-gray-200  group-hover:${showBin ? 'bg-red-100 ' : 'bg-purple-50 '}`}>
                  <div>
                    <h3 className="font-bold text-gray-800  text-lg group-hover:text-purple-700 :text-purple-400 transition">{submission.name}</h3>
                    <div className="flex flex-wrap gap-2 items-center mt-1">
                      <span className="text-sm text-gray-600 ">{submission.email}</span>
                      {submission.phone && (
                        <span className="text-xs bg-gray-100  text-gray-600  px-2 py-1 rounded">📞 {submission.phone}</span>
                      )}
                      {submission.subject && (
                        <span className="text-xs bg-purple-100  text-purple-700  px-2 py-1 rounded font-semibold">{submission.subject}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-2 md:mt-0">
                    <span className="flex items-center gap-1 text-sm text-gray-500 ">
                      <FaCalendarAlt />
                      <span>{formatDate(submission.date)}</span>
                    </span>
                    {showBin ? (
                      <>
                        <button
                          onClick={e => {e.stopPropagation(); handleRestore(submission.id);}}
                          className="ml-2 text-green-600 hover:text-green-800 p-1 rounded-full border border-green-100  bg-green-50 "
                          title="Restore"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h4V6m0 4l5.5 5.5m0 0L21 9m-7.5 6.5V3" /></svg>
                        </button>
                        <button
                          onClick={e => {e.stopPropagation(); handlePermanentDelete(submission.id);}}
                          className="ml-2 text-red-500 hover:text-red-700 p-1 rounded-full border border-red-100  bg-red-50 "
                          title="Delete Permanently"
                        >
                          <FaTrash />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={e => {e.stopPropagation(); handleDelete(submission.id);}}
                        className="ml-2 text-red-500 hover:text-red-700 p-1 rounded-full border border-red-100  bg-red-50 "
                        title="Delete Submission"
                      >
                        <FaTrash />
                      </button>
                    )}
                  </div>
                  <div className="absolute top-2 right-2 text-xs text-gray-400  opacity-0 group-hover:opacity-100 transition">Click for details</div>
                </div>
                <div className="p-4 bg-white  line-clamp-3 text-gray-700 ">
                  {submission.message.length > 120 ? submission.message.slice(0,120) + '...' : submission.message}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Modal for viewing details */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="bg-white  border border-slate-300  rounded-xl shadow-2xl p-8 max-w-lg w-full relative animate-fadeIn text-slate-800 ">
            <button className="absolute top-3 right-3 text-gray-400  hover:text-gray-700 :text-slate-300 text-xl" onClick={()=>setModal(null)}>&times;</button>
            <h2 className="text-2xl font-bold mb-4 text-purple-700  flex items-center gap-2"><FaEnvelope /> Submission Details</h2>
            <div className="space-y-2">
              <div><span className="font-semibold text-slate-800 ">Name:</span> {modal.name}</div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-800 ">Email:</span> {modal.email}
                <button className="text-xs px-2 py-1 bg-gray-100  rounded hover:bg-purple-100 :bg-purple-950/50 text-gray-800 " onClick={()=>{navigator.clipboard.writeText(modal.email)}}>Copy</button>
                <a href={`mailto:${modal.email}`} className="text-xs px-2 py-1 bg-green-100  text-green-800  rounded hover:bg-green-200 :bg-green-950/50">Reply</a>
              </div>
              {modal.phone && (
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-800 ">Phone:</span> {modal.phone}
                  <button className="text-xs px-2 py-1 bg-gray-100  rounded hover:bg-purple-100 :bg-purple-950/50 text-gray-800 " onClick={()=>{navigator.clipboard.writeText(modal.phone)}}>Copy</button>
                </div>
              )}
              {modal.subject && (
                <div><span className="font-semibold text-slate-800 ">Subject:</span> <span className="bg-purple-100  text-purple-700  px-2 py-1 rounded font-semibold ml-1">{modal.subject}</span></div>
              )}
              <div><span className="font-semibold text-slate-800 ">Date:</span> {formatDate(modal.date)}</div>
              <div className="mt-4"><span className="font-semibold text-slate-800 ">Message:</span>
                <div className="bg-gray-50  border border-gray-200  rounded p-3 mt-1 text-gray-700  whitespace-pre-wrap">{modal.message}</div>
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <button className="bg-purple-600  hover:bg-purple-700 :bg-purple-600 text-white px-4 py-2 rounded" onClick={()=>setModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-8 gap-2">
          {[...Array(totalPages)].map((_,i)=>(
            <button
              key={i}
              className={`px-3 py-1 rounded ${page===i+1 ? 'bg-purple-600  text-white' : 'bg-gray-100  text-gray-700 '} font-semibold`}
              onClick={()=>setPage(i+1)}
            >{i+1}</button>
          ))}
        </div>
      )}
    </div>
  );
}

export default ContactSubmissions;
